import { useState } from "react"

import { useTraza } from "@/api/queries"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatearCoste, formatearMs, formatearNumero } from "@/shared/format"

export function TracesPage() {
  const [escrito, setEscrito] = useState("")
  const [buscado, setBuscado] = useState("")

  // La query se dispara con `buscado`, no con lo que se está escribiendo: sin esto,
  // cada tecla lanzaría una petición con un UUID a medias.
  const traza = useTraza(buscado)

  const total = (traza.data ?? []).reduce((suma, p) => suma + p.cost_micros, 0)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Trazas</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Todas las llamadas de una operación lógica. Hoy suele ser una; cuando lleguen
          loops de agente y RAG serán varias, y esto responderá cuánto costó la operación
          entera.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form
            className="flex items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              setBuscado(escrito.trim())
            }}
          >
            <div className="flex-1 space-y-2">
              <Label htmlFor="trace">Identificador de traza</Label>
              <Input
                id="trace"
                value={escrito}
                onChange={(e) => setEscrito(e.target.value)}
                placeholder="01a01a00-0000-7000-8000-0000000000ff"
                className="font-mono"
              />
            </div>
            <Button type="submit" disabled={escrito.trim() === ""}>
              Buscar
            </Button>
          </form>
          <p className="mt-2 text-xs text-muted-foreground">
            Es el valor que la aplicación manda en la cabecera <code>X-Alpaca-Trace-Id</code>.
          </p>
        </CardContent>
      </Card>

      {buscado !== "" && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle>Pasos</CardTitle>
            {traza.data !== undefined && traza.data.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {traza.data.length} {traza.data.length === 1 ? "llamada" : "llamadas"} ·{" "}
                <span className="font-medium text-foreground tabular-nums">
                  {formatearCoste(total)}
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {traza.isPending ? (
              <Skeleton className="h-32" />
            ) : traza.isError ? (
              <p className="text-sm text-destructive">
                No se pudo buscar. Comprueba que el identificador sea un UUID válido.
              </p>
            ) : traza.data.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No hay llamadas con esa traza.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Paso</TableHead>
                      <TableHead>Alias</TableHead>
                      <TableHead>Funcionalidad</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Input</TableHead>
                      <TableHead className="text-right">Output</TableHead>
                      <TableHead className="text-right">TTFT</TableHead>
                      <TableHead className="text-right">Coste</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {traza.data.map((p, i) => (
                      <TableRow key={`${p.step ?? i}-${p.created_at}`}>
                        <TableCell className="tabular-nums">{p.step ?? "—"}</TableCell>
                        <TableCell className="font-medium">{p.alias}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.feature_tag ?? "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            <Badge
                              variant={p.status === "completed" ? "secondary" : "outline"}
                            >
                              {p.status}
                            </Badge>
                            {p.metering_quality !== "exact" && (
                              <Badge variant="destructive">{p.metering_quality}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatearNumero(p.input_cached + p.input_uncached)}
                          {p.input_cached > 0 && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({formatearNumero(p.input_cached)} en caché)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatearNumero(p.output)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatearMs(p.ttft_ms)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatearCoste(p.cost_micros)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
